
import { supabase } from '@/lib/supabase/client';
import { executeWithTimeout } from '@/lib/supabase/queryUtils';
import { debugError } from '@/utils/debugLogger';

/**
 * Service for fetching, updating, and managing user profiles
 */
export const profileService = {
  /**
   * Fetch a user's profile from the database
   * @param userId User ID
   */
  fetchUserProfile: async (userId: string) => {
    if (!userId) {
      debugError('auth', 'No user ID provided to fetchUserProfile');
      return null;
    }

    try {
      const { data, error } = await executeWithTimeout(async () => {
        const response = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', userId)
          .single();
          
        return { 
          data: response.data,
          error: response.error
        };
      });

      if (error) {
        debugError('auth', 'Error fetching user profile:', error);
        return null;
      }

      return data;
    } catch (error) {
      debugError('auth', 'Exception in fetchUserProfile:', error);
      return null;
    }
  },

  /**
   * Create a new user profile
   * @param userId User ID
   * @param email User email
   * @param name User name
   */
  createUserProfile: async (userId: string, email: string, name: string) => {
    if (!userId) {
      debugError('auth', 'No user ID provided to createUserProfile');
      return false;
    }

    try {
      const { error } = await executeWithTimeout(async () => {
        const response = await supabase
          .from('user_profiles')
          .insert({
            user_id: userId,
            email,
            name,
            created_at: new Date()
          });
          
        return { 
          data: response.data,
          error: response.error
        };
      });

      if (error) {
        debugError('auth', 'Error creating user profile:', error);
        return false;
      }

      return true;
    } catch (error) {
      debugError('auth', 'Exception in createUserProfile:', error);
      return false;
    }
  }
};
